import React, { useCallback, useEffect, useState } from "react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import api from "@/lib/api";
import { fmtEUR } from "@/lib/format";
import { CARD, Label, Loader, ErrorRetry } from "@/components/common";

const pct = (a, b) => (b ? Math.round(((a - b) / Math.abs(b)) * 1000) / 10 : null);
const ABBR = ["", "Janv.", "Févr.", "Mars", "Avr.", "Mai", "Juin", "Juil.", "Août", "Sept.", "Oct.", "Nov.", "Déc."];
const mLabel = (m) => `${ABBR[+m.slice(5, 7)]} ${m.slice(2, 4)}`;
const Pct = ({ v }) => <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${v > 0 ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>{v > 0 ? "+" : ""}{v} %</span>;

const kfmt = (v) => (Math.abs(v) >= 1000 ? `${Math.round(v / 100) / 10}k` : `${Math.round(v)}`);

function Kpi({ label, value, sub, testid, onClick }) {
  return (
    <button type="button" onClick={onClick} disabled={!onClick} data-testid={testid}
      className={`${CARD} p-5 text-left transition-transform enabled:hover:-translate-y-0.5`}>
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">{label}</p>
      <p className="mt-1.5 font-display text-2xl font-bold tabular-nums">{value}</p>
      {sub && <div className="mt-1 text-xs text-gray-500">{sub}</div>}
    </button>
  );
}

function AccountBars({ rows }) {
  const max = Math.max(...rows.map((r) => r.amount), 1);
  return (
    <div className="space-y-2.5" data-testid="pil-by-account">
      {rows.slice(0, 10).map((r) => (
        <div key={r.code}>
          <div className="flex justify-between gap-3 text-sm"><span className="truncate"><b className="tabular-nums text-[#065F46]">{r.code}</b> {r.label}</span><span className="shrink-0 font-semibold tabular-nums">{fmtEUR(r.amount)}</span></div>
          <div className="mt-1 h-1.5 rounded-full bg-gray-100"><div className="h-1.5 rounded-full bg-[#B08D57]" style={{ width: `${(r.amount / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

export function Overview({ onNav }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const load = useCallback(() => { setErr(false); api.get("/pilotage/overview").then((r) => setD(r.data)).catch(() => setErr(true)); }, []);
  useEffect(() => { load(); }, [load]);
  if (err) return <ErrorRetry onRetry={load} />;
  if (!d) return <Loader />;
  const k = d.kpis;
  const data = d.months.map((m) => ({ name: mLabel(m.month), "Achats HT (factures)": m.achats_ht, "Dépenses enregistrées": m.depenses }));
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi testid="kpi-month" label="Achats du mois" value={fmtEUR(k.month_ht)} sub={k.prev_month_ht ? <span className="flex items-center gap-1">vs mois précédent <Pct v={pct(k.month_ht, k.prev_month_ht)} /></span> : "Rien le mois précédent"} />
        <Kpi testid="kpi-ytd" label="Depuis janvier" value={fmtEUR(k.ytd_ht)} sub={k.prev_ytd_ht ? <span className="flex items-center gap-1">vs N-1 <Pct v={pct(k.ytd_ht, k.prev_ytd_ht)} /></span> : "Pas de comparaison N-1"} />
        <Kpi testid="kpi-unpaid" label="Reste à régler" value={fmtEUR(k.unpaid_ttc)} sub={`${k.unpaid_count} facture(s) TTC · ${k.overdue_count} en retard`} />
        <Kpi testid="kpi-alerts" label="Alertes" value={k.alerts} sub={`${k.to_validate} facture(s) à valider en pré-compta`} onClick={() => onNav("alerts")} />
      </div>
      <div className={`${CARD} p-6`}>
        <Label className="mb-1">Achats et dépenses — 12 mois</Label>
        <p className="mb-2 text-xs text-gray-400">Barres : achats HT lus sur les factures fournisseurs (échelle de gauche). Courbe : toutes les dépenses enregistrées, salaires et loyers compris (échelle de droite).</p>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 10, right: 0, left: -6, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F2F4" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="a" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} tickFormatter={kfmt} />
              <YAxis yAxisId="d" orientation="right" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} tickFormatter={kfmt} />
              <Tooltip formatter={(v) => fmtEUR(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="a" dataKey="Achats HT (factures)" fill="#B08D57" radius={[6, 6, 0, 0]} />
              <Line yAxisId="d" dataKey="Dépenses enregistrées" stroke="#065F46" strokeWidth={2} dot={{ r: 2 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid gap-5 lg:grid-cols-2">
        <div className={`${CARD} p-6`}><Label className="mb-3">Répartition par compte (12 mois)</Label>
          {d.by_account.length ? <AccountBars rows={d.by_account} /> : <p className="py-8 text-center text-sm text-gray-400">Aucune facture sur la période.</p>}</div>
        <div className={`${CARD} p-6`}><Label className="mb-3">Principaux fournisseurs (12 mois)</Label>
          <div className="divide-y divide-gray-100" data-testid="pil-top-suppliers">
            {d.top_suppliers.map((s) => (
              <div key={s.key} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                <div className="min-w-0"><p className="truncate font-semibold">{s.name}</p><p className="text-xs text-gray-400">{s.count} facture(s) · {s.main_account} {s.main_account_label}</p></div>
                <span className="shrink-0 font-semibold tabular-nums">{fmtEUR(s.total_ht)}</span>
              </div>
            ))}
            {!d.top_suppliers.length && <p className="py-8 text-center text-sm text-gray-400">Aucun fournisseur pour l'instant.</p>}
          </div>
          <button type="button" onClick={() => onNav("suppliers")} data-testid="pil-see-suppliers" className="mt-3 text-sm font-semibold text-[#065F46] hover:underline">Voir toutes les fiches fournisseurs</button>
        </div>
      </div>
    </div>
  );
}
